/**
 * Extended Hotspot – toggle product popups
 */
(function () {
  'use strict';

  const VIEWPORT_MARGIN = 12;

  function resetPopupPosition(popup) {
    if (!popup) return;
    popup.classList.remove(
      'ehsp__popup--align-start',
      'ehsp__popup--align-end',
      'ehsp__popup--align-top',
      'ehsp__popup--align-bottom',
      'ehsp__popup--flip-left',
      'ehsp__popup--flip-right'
    );
    popup.style.removeProperty('--ehsp-popup-shift-x');
    popup.style.removeProperty('--ehsp-popup-shift-y');
  }

  function closeAll(root) {
    root.querySelectorAll('[data-ehsp-hotspot].is-active').forEach((item) => {
      item.classList.remove('is-active');
      const trigger = item.querySelector('[data-ehsp-trigger]');
      const popup = item.querySelector('[data-ehsp-popup]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (popup) {
        popup.setAttribute('aria-hidden', 'true');
        resetPopupPosition(popup);
      }
    });
  }

  function measurePopup(popup) {
    return popup.getBoundingClientRect();
  }

  function applyPopupShift(popup, axis, value) {
    const prop = axis === 'y' ? '--ehsp-popup-shift-y' : '--ehsp-popup-shift-x';
    if (value) {
      popup.style.setProperty(prop, `${value}px`);
    } else {
      popup.style.removeProperty(prop);
    }
  }

  function getPopupAxis(popup) {
    if (popup.classList.contains('ehsp__popup--left') || popup.classList.contains('ehsp__popup--right')) {
      return 'horizontal';
    }
    return 'vertical';
  }

  function positionVerticalPopup(popup) {
    let rect = measurePopup(popup);
    const viewportWidth = window.innerWidth;

    if (rect.right > viewportWidth - VIEWPORT_MARGIN) {
      popup.classList.add('ehsp__popup--align-end');
      rect = measurePopup(popup);
    }

    if (rect.left < VIEWPORT_MARGIN) {
      popup.classList.remove('ehsp__popup--align-end');
      popup.classList.add('ehsp__popup--align-start');
      rect = measurePopup(popup);
    }

    let shift = 0;
    if (rect.right > viewportWidth - VIEWPORT_MARGIN) {
      shift += viewportWidth - VIEWPORT_MARGIN - rect.right;
    }
    if (rect.left + shift < VIEWPORT_MARGIN) {
      shift += VIEWPORT_MARGIN - (rect.left + shift);
    }

    applyPopupShift(popup, 'x', shift);
  }

  function positionHorizontalPopup(popup) {
    let rect = measurePopup(popup);
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const opensLeft = popup.classList.contains('ehsp__popup--left');

    if (opensLeft && rect.left < VIEWPORT_MARGIN) {
      popup.classList.add('ehsp__popup--flip-right');
      rect = measurePopup(popup);
    } else if (!opensLeft && rect.right > viewportWidth - VIEWPORT_MARGIN) {
      popup.classList.add('ehsp__popup--flip-left');
      rect = measurePopup(popup);
    }

    if (rect.bottom > viewportHeight - VIEWPORT_MARGIN) {
      popup.classList.add('ehsp__popup--align-bottom');
      rect = measurePopup(popup);
    }

    if (rect.top < VIEWPORT_MARGIN) {
      popup.classList.remove('ehsp__popup--align-bottom');
      popup.classList.add('ehsp__popup--align-top');
      rect = measurePopup(popup);
    }

    let shiftY = 0;
    if (rect.bottom > viewportHeight - VIEWPORT_MARGIN) {
      shiftY += viewportHeight - VIEWPORT_MARGIN - rect.bottom;
    }
    if (rect.top + shiftY < VIEWPORT_MARGIN) {
      shiftY += VIEWPORT_MARGIN - (rect.top + shiftY);
    }

    let shiftX = 0;
    if (rect.right > viewportWidth - VIEWPORT_MARGIN) {
      shiftX += viewportWidth - VIEWPORT_MARGIN - rect.right;
    }
    if (rect.left + shiftX < VIEWPORT_MARGIN) {
      shiftX += VIEWPORT_MARGIN - (rect.left + shiftX);
    }

    applyPopupShift(popup, 'y', shiftY);
    applyPopupShift(popup, 'x', shiftX);
  }

  function positionPopup(item) {
    const popup = item.querySelector('[data-ehsp-popup]');
    if (!popup) return;

    resetPopupPosition(popup);

    requestAnimationFrame(() => {
      if (getPopupAxis(popup) === 'horizontal') {
        positionHorizontalPopup(popup);
      } else {
        positionVerticalPopup(popup);
      }
    });
  }

  function openHotspot(item) {
    const trigger = item.querySelector('[data-ehsp-trigger]');
    const popup = item.querySelector('[data-ehsp-popup]');
    item.classList.add('is-active');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (popup) popup.setAttribute('aria-hidden', 'false');
    positionPopup(item);
  }

  function initHotspots(root) {
    const stage = root.querySelector('[data-ehsp-stage]');
    if (!stage || stage.dataset.ehspReady === 'true') return;

    const items = stage.querySelectorAll('[data-ehsp-hotspot]');

    items.forEach((item) => {
      const trigger = item.querySelector('[data-ehsp-trigger]');
      if (!trigger) return;

      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const isActive = item.classList.contains('is-active');
        closeAll(root);
        if (!isActive) openHotspot(item);
      });
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) closeAll(root);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAll(root);
    });

    window.addEventListener(
      'resize',
      () => {
        const active = stage.querySelector('[data-ehsp-hotspot].is-active');
        if (active) positionPopup(active);
      },
      { passive: true }
    );

    stage.dataset.ehspReady = 'true';

    const initialActive = stage.querySelector('[data-ehsp-hotspot].is-active');
    if (initialActive) positionPopup(initialActive);
  }

  function initSection(sectionRoot) {
    const root = sectionRoot.querySelector('[data-extended-hotspot]');
    if (root) initHotspots(root);
  }

  function initAll() {
    document.querySelectorAll('.extended-hotspot-section').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (section.classList.contains('extended-hotspot-section') || section.querySelector('[data-extended-hotspot]')) {
      const stage = section.querySelector('[data-ehsp-stage]');
      if (stage) stage.dataset.ehspReady = 'false';
      initSection(section);
    }
  });
})();
